import React, { useMemo, useState } from "react";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { GetNotificationsResponse, NotificationDTO } from "../../types/requestsOutputs";
import {
    FaExclamationCircle,
    FaArrowCircleRight,
    FaInfoCircle,
    FaGlobe,
    FaMapSigns,
    FaHeadphones,
    FaClock,
    FaCalendarAlt,
    FaLink,
    FaExternalLinkAlt,
} from "react-icons/fa";
import { IconType } from "react-icons";
import { deleteNotification, updateNotification } from "../../api/notification";
import { useTranslation } from "react-i18next";
import NotificationCompactPreview from "../../pages/notification/NotificationCompactPreview";
import DeleteModal from "../modals/DeleteModal";
import { ReactComponent as DeleteIcon } from "../../assets/images/delete-bin-line.svg";
import { Tooltip } from "@mui/material";
import ToggleSwitch from "../common/ToggleSwitch";
import styles from "./Notification.module.css";
import previewStyles from "../../pages/notification/NotificationPreview.module.css";

dayjs.extend(utc);

interface Props {
    notifications: GetNotificationsResponse;
    locale?: string; // e.g. 'EN', 'FR'
    refetch?: () => Promise<void>; // Function to refetch notifications
}

export const NotificationPanel: React.FC<Props> = ({ notifications, locale = "EN", refetch }) => (
    <div className={styles.panel}>
        {notifications
            ?.slice()
            .reverse()
            .map((notif) => (
                <NotificationCard key={notif.id} notif={notif} locale={locale} refetch={refetch} />
            ))}
    </div>
);

interface CardProps {
    notif: NotificationDTO;
    locale: string;
    refetch?: () => Promise<void>; // Function to refetch notifications
}

const NotificationCard: React.FC<CardProps> = ({ notif, locale, refetch }) => {
    const { t: rawT, i18n } = useTranslation();
    const t = rawT as any;

    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [isActive, setIsActive] = useState(notif.isActive);
    const [isToggling, setIsToggling] = useState(false);

    // Handle toggle active status
    const handleToggleActive = async () => {
        if (isToggling) return;

        try {
            setIsToggling(true);
            const token = localStorage.getItem("token");
            if (!token) {
                console.error("No token found");
                return;
            }

            const newActiveState = !isActive;
            await updateNotification({
                token,
                id: notif.id,
                payload: { isActive: newActiveState },
            });

            setIsActive(newActiveState);
            if (refetch) {
                await refetch();
            }
        } catch (error) {
            console.error("Failed to toggle notification status", error);
        } finally {
            setIsToggling(false);
        }
    };

    // Determine redirect URL if any
    const redirectUrl =
        notif.deeplink ||
        notif.urlNotification ||
        (notif.productId ? `/products/${notif.productId}` : null) ||
        (notif.infoChaudeId ? `/info/${notif.infoChaudeId}` : null);

    // Notification type determination
    const notificationType = useMemo(() => {
        if (notif.isUrgent) return "urgent";
        if (redirectUrl) return "redirection";
        return "information";
    }, [notif.isUrgent, redirectUrl]);

    // Primary icon
    const PrimaryIcon: IconType = useMemo(() => {
        switch (notificationType) {
            case "urgent":
                return FaExclamationCircle;
            case "redirection":
                return FaArrowCircleRight;
            default:
                return FaInfoCircle;
        }
    }, [notificationType]);

    // Get localized track name
    const trackLocalizedName = useMemo(() => {
        if (!notif.track) return null;
        // Try to find localized name for the current locale
        const trackLocalized = notif.track.trackLocalizeds?.find(
            (tl) => tl.language.toUpperCase() === locale.toUpperCase(),
        );
        if (trackLocalized?.name) return trackLocalized.name;
        // Fallback to first localized name
        if (notif.track.trackLocalizeds?.[0]?.name) return notif.track.trackLocalizeds[0].name;
        // Final fallback to internal name
        return notif.track.internalName;
    }, [notif.track, locale]);

    // Build scope and condition items for display
    const scopeItems: { icon: IconType; label: string }[] = [];
    if (notif.allApplication) {
        scopeItems.push({ icon: FaGlobe, label: t("wholeApp") });
    }
    if (notif.trackId && notif.track && trackLocalizedName) {
        scopeItems.push({ icon: FaMapSigns, label: trackLocalizedName });
    }

    // Build trigger condition items
    const triggerItems: { icon: IconType; label: string }[] = [];
    if (notif.hour) {
        triggerItems.push({ icon: FaClock, label: `${t("hour")}: ${notif.hour}` });
    }
    if (notif.timeSpentInApp != null) {
        const minutesKey = notif.timeSpentInApp <= 1 ? "afterMinutesOnTrackSingular" : "afterMinutesOnTrackPlural";
        triggerItems.push({ icon: FaClock, label: t(minutesKey, { count: notif.timeSpentInApp }) });
    }
    if (notif.audioListened != null) {
        const storiesKey = notif.audioListened <= 1 ? "afterStoriesListenedSingular" : "afterStoriesListenedPlural";
        triggerItems.push({ icon: FaHeadphones, label: t(storiesKey, { count: notif.audioListened }) });
    }

    // Get product localized name for in-app link
    const productLocalizedName = useMemo(() => {
        if (!notif.product) return null;

        // If product type is TRACK, get localized name from track
        if (notif.product.type === "TRACK" && notif.product.track) {
            const trackLocalized = notif.product.track.trackLocalizeds?.find(
                (tl) => tl.language.toUpperCase() === locale.toUpperCase(),
            );
            if (trackLocalized?.name) return trackLocalized.name;
            if (notif.product.track.trackLocalizeds?.[0]?.name) return notif.product.track.trackLocalizeds[0].name;
            return notif.product.track.internalName;
        }

        // If product type is ACTIVITYPCV, get localized name from activityPCV
        if (notif.product.type === "ACTIVITYPCV" && notif.product.activityPCV) {
            const activityLocalized = notif.product.activityPCV.activityPCVLocalized?.find(
                (al) => al.language.toUpperCase() === locale.toUpperCase(),
            );
            if (activityLocalized?.name) return activityLocalized.name;
            if (notif.product.activityPCV.activityPCVLocalized?.[0]?.name)
                return notif.product.activityPCV.activityPCVLocalized[0].name;
            return notif.product.activityPCV.internalName;
        }

        // Fallback to product internal name
        return notif.product.internalName || notif.productId;
    }, [notif.product, notif.productId, locale]);

    // Build redirection items
    const redirectionItems: { icon: IconType; label: string }[] = [];
    if (notif.urlNotification) {
        redirectionItems.push({ icon: FaExternalLinkAlt, label: `${t("externalLink")}: ${notif.urlNotification}` });
    }
    if (notif.productId && productLocalizedName) {
        redirectionItems.push({ icon: FaLink, label: `${t("inAppLink")}: ${productLocalizedName}` });
    } else if (notif.deeplink) {
        redirectionItems.push({ icon: FaLink, label: `${t("inAppLink")}: ${notif.deeplink}` });
    }

    // Localization - find the best match for the user's locale
    const localized = useMemo(() => {
        if (!notif.notificationLocalized || notif.notificationLocalized.length === 0) {
            return { shortMessage: "", longMessage: "", language: "" };
        }
        // Try to find exact match first
        const exactMatch = notif.notificationLocalized.find((n) => n.language.toUpperCase() === locale.toUpperCase());
        if (exactMatch) return exactMatch;

        // Fallback to first available
        return notif.notificationLocalized[0];
    }, [notif.notificationLocalized, locale]);

    const handleCardClick = () => {
        if (isDeleteModalOpen) return;
        // Don't redirect when clicking card for now - just expand details
    };

    // On delete handler
    const handleDelete = async () => {
        try {
            const token = localStorage.getItem("token");
            if (!token) {
                console.error("No token found");
                return;
            }
            await deleteNotification({ id: notif.id, token });
            if (refetch) {
                await refetch();
            }
        } catch (error) {
            console.error("Failed to delete notification", error);
        }
    };

    // Status badge colors and labels
    const getTypeStyles = () => {
        switch (notificationType) {
            case "urgent":
                return { bg: "#fee2e2", color: "#dc2626", label: t("urgentNotification") };
            case "redirection":
                return { bg: "#dbeafe", color: "#2563eb", label: t("purchaseNotification") };
            default:
                return { bg: "#e0f2fe", color: "#0891b2", label: t("informationNotification") };
        }
    };

    const typeStyles = getTypeStyles();

    // Date formatting based on locale
    const formatDate = (date: string) => {
        const d = dayjs.utc(date);
        return i18n.language?.startsWith("fr") ? d.format("D MMM YYYY") : d.format("MMM D, YYYY");
    };

    return (
        <div className={styles.card} onClick={handleCardClick}>
            <div className={styles.cardInner}>
                <div className={styles.cardContent}>
                    <div
                        className={styles.typeBadge}
                        style={{ backgroundColor: typeStyles.bg, color: typeStyles.color }}
                    >
                        <PrimaryIcon size={14} style={{ marginRight: 6 }} />
                        {typeStyles.label}
                    </div>

                    <h3 className={styles.cardTitle}>{localized.shortMessage || t("noTitle")}</h3>

                    {notif.startDate && notif.endDate && (
                        <div className={styles.dateRange}>
                            <FaCalendarAlt size={12} style={{ marginRight: 6, opacity: 0.7 }} />
                            <span>
                                {formatDate(notif.startDate)} – {formatDate(notif.endDate)}
                            </span>
                        </div>
                    )}

                    {localized.longMessage && <p className={styles.cardDescription}>{localized.longMessage}</p>}

                    <div className={styles.badgesRow}>
                        {scopeItems.map(({ icon: IconComp, label }, idx) => (
                            <span key={`scope-${idx}`} className={styles.specBadge}>
                                <IconComp size={12} style={{ marginRight: 4 }} />
                                {label}
                            </span>
                        ))}
                        {triggerItems.map(({ icon: IconComp, label }, idx) => (
                            <span key={`trigger-${idx}`} className={styles.specBadge}>
                                <IconComp size={12} style={{ marginRight: 4 }} />
                                {label}
                            </span>
                        ))}
                        {redirectionItems.map(({ icon: IconComp, label }, idx) => (
                            <span key={`redirect-${idx}`} className={`${styles.specBadge} ${styles.specBadgeLink}`}>
                                <IconComp size={12} style={{ marginRight: 4 }} />
                                {label}
                            </span>
                        ))}
                    </div>
                </div>

                <div className={styles.cardPreviews}>
                    <NotificationCompactPreview
                        shortText={localized.shortMessage}
                        fullText={localized.longMessage}
                        logoUrl={notif.logoNotification}
                        notificationType={
                            notificationType === "urgent"
                                ? "urgent"
                                : notificationType === "redirection"
                                  ? "redirection"
                                  : "information"
                        }
                        plain
                    />

                    {notificationType !== "urgent" && (
                        <div className={styles.expandedPreview}>
                            <div className={`${previewStyles.modalPreview} ${styles.modalPreviewSmall}`}>
                                <div className={previewStyles.modalTitle}>
                                    {localized.shortMessage || t("notificationTitle")}
                                </div>
                                <div className={previewStyles.modalMessage}>
                                    {localized.longMessage || t("notificationContent")}
                                </div>
                                {notificationType === "redirection" && (
                                    <div className={previewStyles.modalButtons}>
                                        <button className={styles.modalBtnPrimarySmall}>{t("learnMore")}</button>
                                        <button className={styles.modalBtnSecondarySmall}>{t("noThanks")}</button>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </div>

            <div className={styles.cardFooterActions}>
                <div className={styles.statusToggle}>
                    <span className={`${styles.statusLabel} ${isActive ? styles.statusLabelOn : ""}`}>
                        {isActive ? t("active") : t("inactive")}
                    </span>
                    <ToggleSwitch checked={isActive} onChange={handleToggleActive} />
                </div>

                <Tooltip title={t("delete")} arrow>
                    <DeleteIcon
                        className={styles.deleteIcon}
                        onClick={(e) => {
                            e.stopPropagation();
                            setIsDeleteModalOpen(true);
                        }}
                    />
                </Tooltip>
            </div>

            <DeleteModal
                isOpen={isDeleteModalOpen}
                onClose={() => setIsDeleteModalOpen(false)}
                onConfirm={handleDelete}
                message={t("deleteNotificationConfirmation")}
                title={t("deleteNotification")}
            />
        </div>
    );
};
